import { filesize } from 'filesize';
import moment from 'moment';
import { PlanEntitlementKey } from '../../api/graphql/generated';
import { IntercomArticle } from '../../config/intercom';
import { i18nKeyType } from '../../i18n';

export interface PlanFeatureData {
  articleId?: IntercomArticle;
  description?: i18nKeyType;
  titleKey?: i18nKeyType;
  img?: string;
  highlight?: {
    order: number;
    titleKey: i18nKeyType;
    icon?: string;
  };
  valueFormatter?: (value: any) => any;
}

export enum PlanFeatureGroupType {
  WELDING,
}

export const PlanFeatureDetails: Record<
  PlanEntitlementKey,
  PlanFeatureData | undefined
> = {
  [PlanEntitlementKey.AI_RECOMMENDATIONS]: {
    articleId: IntercomArticle.AI_RECOMMENDATIONS,
    description: 'plan:ai_recommendations_feature_description',
    titleKey: 'plan:ai_recommendations_feature_title',
    highlight: {
      order: 50,
      titleKey: 'plan:ai_recommendations_feature',
    },
  },
  [PlanEntitlementKey.ATTACHMENT_MAX_FILE_UPLOAD_SIZE]: {},
  [PlanEntitlementKey.ATTACHMENT_MAX_TENANT_STORAGE_SIZE]: {
    highlight: {
      order: 100,
      titleKey: 'plan:storage_limit',
    },
    valueFormatter: (value: number) => {
      // Tenant storage size uses megabytes (1024 KB) as its unit of measure.
      // Convert it to bytes for the filesize library.
      const bytes = value * 1024 * 1024;
      return filesize(bytes, {
        base: 2,
        standard: 'jedec',
      });
    },
  },
  [PlanEntitlementKey.CHART_EXPORT]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:chart_export_feature_description',
    titleKey: 'plan:chart_export_feature_title',
    highlight: {
      order: 60,
      titleKey: 'plan:chart_export_feature',
    },
  },
  [PlanEntitlementKey.CHART_FULLSCREEN]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:chart_fullscreen_feature_description',
    titleKey: 'plan:chart_fullscreen_feature_title',
    highlight: {
      order: 70,
      titleKey: 'plan:chart_fullscreen_feature',
    },
  },
  [PlanEntitlementKey.CHART_MAX_TIMEFRAME]: {
    articleId: IntercomArticle.PRO_PLAN,
    highlight: {
      order: 80,
      titleKey: 'plan:chart_max_timeframe_feature',
    },
    valueFormatter: (value: number) => {
      return moment.duration(value, 'milliseconds').asDays();
    },
  },
  [PlanEntitlementKey.CHART_ROW_LIMIT]: {
    articleId: IntercomArticle.PRO_PLAN,
  },
  [PlanEntitlementKey.ENHANCED_PLAYLIST]: {
    articleId: IntercomArticle.PRO_PLAN,
    titleKey: 'plan:enhanced_playlist_title',
    description: 'plan:enhanced_playlist_description',
    highlight: {
      order: 40,
      titleKey: 'plan:enhanced_playlist_feature',
    },
  },
  [PlanEntitlementKey.ENHANCED_USER_ROLES]: {
    // For now these relate to the operator role since we haven't implemented
    // the other roles.
    articleId: IntercomArticle.OPERATOR_ROLE,
    titleKey: 'plan:custom_operator_role_title',
    description: 'plan:custom_operator_role_description',
  },
  [PlanEntitlementKey.EVENT_CHANNEL_WEBHOOK_SERVICE]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:event_channel_webhook_feature_description',
    titleKey: 'plan:event_channel_webhook_feature_title',
  },
  [PlanEntitlementKey.MACHINE_FILE_VERSION_MAX_AGE]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:machine_file_version_max_age_description',
    titleKey: 'plan:machine_file_version_max_age_title',
  },
  [PlanEntitlementKey.SCRATCHPAD_ATTACHMENT]: {
    articleId: IntercomArticle.PRO_PLAN_PART_ATTACHMENTS,
    description: 'plan:scratchpad_attachment_feature_description',
    titleKey: 'plan:scratchpad_attachment_feature_title',
    img: '/assets/pro-plan/part-attachments.png',
    highlight: {
      order: 10,
      titleKey: 'plan:scratchpad_attachment_feature',
    },
  },
  [PlanEntitlementKey.SCRATCHPAD_HISTORY_MAX_AGE]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:scratchpad_history_max_age_description',
    titleKey: 'plan:scratchpad_history_max_age_title',
    highlight: {
      order: 90,
      titleKey: 'plan:scratchpad_history_max_age_feature',
    },
    valueFormatter: (value: number) => {
      return moment.duration(value, 'milliseconds').asDays();
    },
  },
  [PlanEntitlementKey.TEAM_OPERATOR_ROLE]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:operator_role_feature_description',
    titleKey: 'plan:operator_role_feature_title',
    highlight: {
      order: 20,
      titleKey: 'plan:operator_role_feature',
    },
  },
  [PlanEntitlementKey.USER_PASSWORD_AUTH_METHOD]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:user_password_auth_method_description',
    titleKey: 'plan:user_password_auth_method_title',
    highlight: {
      order: 55,
      titleKey: 'plan:user_password_auth_method_feature',
    },
  },
  [PlanEntitlementKey.WELDING_FINE_TUNE]: {
    articleId: IntercomArticle.PRO_PLAN,
    description: 'plan:fine_tune_feature_desc',
    titleKey: 'plan:fine_tune_feature_title',
    highlight: {
      order: 0,
      titleKey: 'plan:fine_tune_feature',
    },
  },
  [PlanEntitlementKey.WELDING_ENHANCED_STITCH]: {
    articleId: IntercomArticle.ENHANCED_STITCH_WELD,
    description: 'plan:enhanced_stitch_weld_feature_description',
    titleKey: 'plan:enhanced_stitch_weld_feature_title',
    highlight: {
      order: 0,
      titleKey: 'plan:enhanced_stitch_weld_feature',
    },
  },
  [PlanEntitlementKey.WELDING_MULTIPASS]: {
    articleId: IntercomArticle.MULTIPASS_WELD,
    description: 'plan:multipass_weld_feature_description',
    titleKey: 'plan:multipass_weld_feature_title',
    highlight: {
      order: 0,
      titleKey: 'plan:multipass_weld_feature',
    },
  },
  [PlanEntitlementKey.WELDING_SEAM_TRACKING]: {
    articleId: IntercomArticle.SEAM_TRACKING,
    description: 'plan:seam_tracking_feature_description',
    titleKey: 'plan:seam_tracking_feature_title',
    highlight: {
      order: 0,
      titleKey: 'plan:seam_tracking_feature',
    },
  },
};
